// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import App from './App';
import VeeValidate from 'vee-validate';
import * as Sentry from "@sentry/vue";
import { default as ENV_VARS, override as setEnvVars } from '@/modules/env-vars.js';
import { setRefreshUrl } from '@/modules/token-refresher';
import gtm from '@/modules/google-tag-manager';

import { createRouter } from '@/router';
import ISRoutes from '@/router/initialstate';
import TekRoutes from '@/router/tekcloud';

import store from '@/store';
import axios from 'axios';
import { mapGetters } from 'vuex';

const version = require('../version.json').version;

Vue.use(VeeValidate,{
	classes: true,
	mode: 'passive',
	dictionary: {
	  en: {
			messages: {
				required: 'Required',
				email: 'Must be a valid email',
				numeric: 'Must be a number',
			},
			custom: {
				postalCode: {
			      	max: 'Must be 8 or fewer characters'
			  	},
				password: {
				  	min: 'Must be 8 or more characters'
			  	},
				emailConfirm: {
					confirmed: 'Must match Email above'
				},
				passwordConfirm: {
					confirmed: 'Must match Password above'
				}
			}
	  	}
  	}
});

Vue.config.productionTip = false;

const rootElement = document.documentElement;

// check for search or pathname that is before hash then replace it
let newHash = window.location.pathname + window.location.search;
if (newHash && newHash !== '/' && newHash !== '#/') {
	window.history.replaceState({}, '', '/#' + newHash);
}

function applyBranding(app) {

	const themePath = '../static/brands/' + ENV_VARS.BRAND_ID; // + ENV_VARS.THEME_URL + ENV_VARS.BRAND_ID;
	// create a link tag for branding
	var cssLink = document.createElement('link');
	cssLink.type = 'text/css';
	cssLink.rel = 'stylesheet';
	cssLink.href = themePath + `/styles.css?v=${version}`;
	document.head.insertBefore(cssLink, null);

	var faviconLink = document.createElement('link');
	faviconLink.rel = 'icon';
	faviconLink.href = themePath + '/fav/favicon.ico';
	document.head.appendChild(faviconLink, null);

	var manifestLink = document.createElement('link');
	manifestLink.rel = 'manifest';
	// keep manifest as local asset, but point to images from theme cdn
	manifestLink.href = '/static/manifests/' + ENV_VARS.ENVIRONMENT +  '/' + ENV_VARS.BRAND_ID + '.json';
	document.head.appendChild(manifestLink, null);

	var browserConfigMeta = document.createElement('meta');
	browserConfigMeta.name = 'msapplication-config';
	browserConfigMeta.content = themePath + '/fav/browserconfig.xml';
	document.head.appendChild(browserConfigMeta, null);

	var appleIconLink = document.createElement('link');
	appleIconLink.sizes = "180x180";
	appleIconLink.rel = 'apple-touch-icon';
	appleIconLink.href = themePath + '/fav/apple-touch-icon-180x180.png';
	document.head.appendChild(appleIconLink, null);

	var icon16Link = document.createElement('link');
	icon16Link.sizes = "16x16";
	icon16Link.type = "image/png";
	icon16Link.rel = 'icon';
	icon16Link.href = themePath + '/fav/favicon-16x16.png';
	document.head.appendChild(icon16Link, null);

	var icon32Link = document.createElement('link');
	icon32Link.sizes = "32x32";
	icon32Link.type = "image/png";
	icon32Link.rel = 'icon';
	icon32Link.href = themePath +  '/fav/favicon-32x32.png';
	document.head.appendChild(icon32Link, null);

	var safariIconLink = document.createElement('link');
	safariIconLink.color = "#ffffff";
	safariIconLink.rel = 'mask-icon';
	safariIconLink.href = themePath +  '/fav/safari-pinned-tab.svg';
	document.head.appendChild(safariIconLink, null);
}

function addGoogleTagManager(w, d, s, l, i) {
	w[l] = w[l] || [];
	w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
	var f = d.getElementsByTagName(s)[0],
		j = d.createElement(s),
		dl = l !== 'dataLayer' ? '&l=' + l : '';
	j.async = true;
	j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
	f.parentNode.insertBefore(j, f);
};

function isSignInRoute(value) {
	return (/^#\/(signin)/i).test(value);
}


/* eslint-disable no-new */
function createApp() {
	const isTek = ENV_VARS.BRAND_ID === 'tekcloud';
	let router = createRouter(isTek ? TekRoutes : ISRoutes);

	return new Vue({
		el: '#app',
		router,
		store,
		components: { App },
		template: '<App/>',
		data() {
			return {
				pointer: '',
				isDev: ENV_VARS.ENVIRONMENT === 'development',
				isTek: isTek
			};
		},
		mounted() {
			let vm = this;
			// provide navigating animation by on first load

			function handleTouchEvent(event) {
				vm.$el.removeEventListener('touchstart', handleTouchEvent);
				vm.$el.removeEventListener('mousemove', handleMouseEvent);
				vm.pointer = 'touch';
			}

			function handleMouseEvent(event) {
				vm.$el.removeEventListener('touchstart', handleTouchEvent);
				vm.$el.removeEventListener('mousemove', handleMouseEvent);
				vm.pointer = 'mouse';
			}

			vm.$el.addEventListener('touchstart', handleTouchEvent);
			vm.$el.addEventListener('mousemove', handleMouseEvent);

			rootElement.classList.remove('navigating', 'initializing');
		},
		created() {
			this.$router.beforeEach((to, from, next) => {
				// start the navigating animation
				rootElement.classList.add('navigating');
				// continue to next page
				next();
	    	});
		    // finish animation after moving router-view
		    this.$router.afterEach((to, from) => {
			    // stop the navigating animation
			    rootElement.classList.remove('navigating');
		    });
			// //set store to strict while in development
			store.strict = ENV_VARS.ENVIRONMENT !== 'production';
		}
	});
}

function resumeSession() {
	var refreshToken = localStorage.getItem('refreshToken');
	if (!refreshToken) {
		// no session to restore, but that's OK
		return Promise.reject();
	}
	// restoring session...
	return store.dispatch('auth/SET_REFRESH_TOKEN', refreshToken)
		.catch((reason) => {
			// ...session invalid
			// Clear the invalid token from localStorage
			localStorage.removeItem('refreshToken');

			// This isn't an app failure; the token is just invalid. Resolve and resume initialization.
			return Promise.reject(reason);
		});
}


function getEnvOverrides() {
	var host = location.hostname;
	var isDev = (host.indexOf('dev-') >= 0 || location.port >= 4000);
	var isTek = (host.indexOf('tekcloud') >= 0 || location.port >= 9090);
	var brandId = isTek ? 'tekcloud' : 'initialstate';
	if (isDev) {
		if (isTek) {
			return {
				ENVIRONMENT:   		'development',
				BRAND_ID:      		'tekcloud',
				BRAND_NAME:	   		'TekCloud',
				TRIAL_DAYS: 		30,
				THEME_URL:     		'https://theme.tekcloud.com/dev/',
				ACCOUNT_URL:   		'https://dev-account.tekcloud.com',
				AUTH_URL:      		'https://dev-auth.tekcloud.com/#/',
				SUPPORT_EMAIL:  	'support@support.tekcloud.com',
				IOT_APP_URL: 		null,
				IOT_PLANS_URL: 		null,
				AUTH_API:      		'https://dev-api-init.tekcloud.com/dev-auth/',
				ACCOUNT_API:   		'https://dev-api-init.tekcloud.com/dev-account/',
				GTM_KEY: 			'GTM-W3G3H78',
				STRIPE_TOKEN: 		'pk_test_xg8APrMDZ6ECU9WQVbRoEA4z00XWOqtQRy',
				// tek specific
				SCOPE_APP_URL: 		'https://dev-scope.tekcloud.com/#/signin/',
				SCOPE_PUBLIC_URL: 	'https://www.tekcloud.com/tekscope/',
				DRIVE_APP_URL: 		'https://dev-drive.tekcloud.com/#/signin/',
				DRIVE_PUBLIC_URL: 	'https://dev-www.tekcloud.com/tekdrive/',
				COUPON_API:    		'https://dev-api-init.tekcloud.com/dev-coupon/',
				TRON_API:      		'https://dev-api-init.tekcloud.com/dev-tron/',
				CONSENT_API: 		'https://account-consent.dev-api.tekcloud.com/'
			};
		}
		return {
			ENVIRONMENT:   		'development',
			BRAND_ID:      		'initialstate',
			BRAND_NAME:	   		'Initial State',
			THEME_URL:     		'https://theme.initialstate.com/dev/',
			ACCOUNT_URL:   		'https://dev-account.initialstate.com',
			AUTH_URL:      		'https://dev-auth.initialstate.com/#/',
			IOT_APP_URL: 		'https://iot.dev-app.initialstate.com/cb/auth/',
			IOT_PLANS_URL: 		'https://dev-www.initialstate.com/#pricing',
			AUTH_API:      		'https://api.init.st/dev-auth/',
			ACCOUNT_API:   		'https://api.init.st/dev-account/',
			GTM_KEY: 			'GTM-5LPQQ3X',
			STRIPE_TOKEN: 		'pk_test_JMy7UgvYHOfBwr9wNQpYzypK'
		};
	}
	if (isTek) {
		return	{
			ENVIRONMENT:   		'production',
			BRAND_ID:      		'tekcloud',
			BRAND_NAME:	   		'TekCloud',
			TRIAL_DAYS: 		30,
			THEME_URL:     		'https://theme.tekcloud.com/prod/',
			ACCOUNT_URL:   		'https://account.tekcloud.com',
			AUTH_URL:      		'https://auth.tekcloud.com/#/',
			SUPPORT_EMAIL:  	'support@support.tekcloud.com',
			IOT_APP_URL: 		null,
			IOT_PLANS_URL:		null,
			AUTH_API:      		'https://api-init.tekcloud.com/auth/',
			ACCOUNT_API:   		'https://api-init.tekcloud.com/account/',
			GTM_KEY: 			'GTM-W3XV7KP',
			STRIPE_TOKEN: 		'pk_live_ofWlzCOhGJyqgVxxydqDh7Ec00NRRvcfzC',
			// tek specific
			SCOPE_APP_URL: 		'https://scope.tekcloud.com/#/signin/',
			SCOPE_PUBLIC_URL: 	'https://www.tekcloud.com/tekscope/',
			DRIVE_APP_URL: 		'https://drive.tekcloud.com/#/signin/',
			DRIVE_PUBLIC_URL: 	'https://www.tekcloud.com/tekdrive/',
			COUPON_API:    		'https://api-init.tekcloud.com/coupon/',
			TRON_API:      		'https://api-init.tekcloud.com/tron/',
			CONSENT_API: 		'https://account-consent.api.tekcloud.com/'
		};
	}
	return {};
}

function initApp() {

	// Propagate ENV_VARS
	// TODO: Make this to a module init process
	// TODO: get META overrides (or use defaults)
	setEnvVars(getEnvOverrides());
	setRefreshUrl((ENV_VARS.AUTH_API || '').replace(/\/$/, '') + '/tokens');
	store.dispatch('SET_SERVICE_ROOTS');

	if (ENV_VARS.GTM_KEY) { // location.hostname !== 'localhost' || /\.tekcloud\.com$/.test(location.hostname)
		gtm.addGoogleTagManager(ENV_VARS.GTM_KEY);
	}

	// set up sentry after ENV_VARS are ready
	Sentry.init({
		Vue,
		dsn: ENV_VARS.SENTRY_DNS,
		environment: ENV_VARS.ENVIRONMENT === 'production' ? 'prod' : (location.hostname.indexOf('dev-') >= 0) ? 'dev' : 'local',
		release: `verona@${version}`,
		beforeSend(event, hint) {
			const error = hint.originalException;
			if (event.environment === 'local') {
				return null;
			}
			return event;
		}
	});


	return resumeSession()
		.catch(() => {
			// Unable to resume session.
			// Since this app may have public routes, the app will now initialize (without auth).
			return null;
		})
		.then(createApp)
		.then((app) => {
			// Apply branding after app styles are loaded
			// TODO: overhaul branding pattern to apply branding immediately
			applyBranding(app);
			return app;
		});
}

// ensure 'navigating' class name is on <html>
rootElement.classList.add('navigating', 'initializing');

// Launch app
initApp()
	.catch((reason) => {
		// The app failed to initialize
		document.documentElement.classList.add('app-fatal-error');
		console.error(reason.response || reason);
	});



// WEBPACK FOOTER //
// ./src/main.js